import { Card, CardContent, Link as MuiLink, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useParams } from "react-router-dom";

import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { useDocumentCapabilities } from "../../auth/permissions.js";
import { getDoc } from "./api.js";
import { DocumentManualForm } from "./DocumentManualForm.js";

/** Edit a single document. Loads the full detail (content, attachments,
 * versions) before mounting the form so the form's defaultValues are
 * correct from the first render — no async reset, no flicker. */
export function DocumentEditPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { canModify } = useDocumentCapabilities();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["docs", "edit", slug],
    queryFn: () => getDoc(slug ?? ""),
    enabled: !!slug,
  });

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14, color: "error.main" }}>
            Could not load document:{" "}
            {error instanceof Error ? error.message : "not found"}
          </Typography>
          <BackButton to="/documentation" sx={{ mt: 1.5 }} />
        </CardContent>
      </Card>
    );
  }

  // Own-records rule for the create tier: a create-tier author may edit only
  // documents they wrote. Full editors pass for any doc. This mirrors the
  // authoritative portal-api gate (which 403s the save regardless) so a
  // direct-URL visit to someone else's doc shows a friendly notice, not a
  // form that fails on submit.
  if (!canModify(data.data)) {
    return (
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            You can only edit documents you created.
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            This document was created by someone else. You can still view it.
          </Typography>
          <MuiLink
            component={RouterLink}
            to={`/documentation/library/${data.data.slug}`}
            underline="hover"
            sx={{ mt: 1.5, display: "inline-block", fontSize: 12, color: "primary.main" }}
          >
            ← View document
          </MuiLink>
        </CardContent>
      </Card>
    );
  }

  return <DocumentManualForm existing={data.data} />;
}
